//#region types
import { CurrencyEnums, FormEnums } from "models/enums";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { AutoCompleteExtraProps, GenericFormSection } from "models/propTypes/genericFormProps";
import { PersonSalaryFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FilterQueryStateHandler } from "models/propTypes/filterPropTypes";
//#endregion
//#region components
import { useCompanyService, useSalaryService } from "serviceHooks";
import { useSalaryPermissions } from "components/salary";
//#endregion
import { EnumHelper, QueryStateHelper, FilterHelper, DateHelper } from "utils/helpers";

function usePersonSalaryReportFilterDefinition() {
  const companyService = useCompanyService();
  const salaryService = useSalaryService();
  const { isSalaryAdmin } = useSalaryPermissions();

  const apiToReportFilterFormatter = async (
    filter: WintSuperkollAPIModel.PersonSalaryEntryFilter
  ): Promise<PersonSalaryFormTypes.Filter> => {
    const reportFilter: PersonSalaryFormTypes.Filter = {
      personId: filter.personIds?.length ? filter.personIds[0] : undefined,
      personName: filter.personIds?.length ? await companyService.getEmployeeName(filter.personIds[0]) : undefined,
      state: filter.states?.map((s) => ({ id: s, value: salaryService.getPersonSalaryStateText(s) })),
      isActive: filter.isActive,
    };

    FilterHelper.setDateRange(
      reportFilter,
      FormFieldConstants.PersonSalary.Filter.YearMonth,
      filter.yearAndMonthFrom ? DateHelper.getDateFromYearMonth(filter.yearAndMonthFrom) : undefined,
      filter.yearAndMonthTo ? DateHelper.getDateFromYearMonth(filter.yearAndMonthTo) : undefined
    );

    FilterHelper.setDateRange(
      reportFilter,
      FormFieldConstants.PersonSalary.Filter.PaymentDate,
      filter.payoutDateFrom,
      filter.payoutDateTo
    );

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.PersonSalary.Filter.TaxesAndFees,
      filter.taxAndFeesFrom,
      filter.taxAndFeesTo
    );

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.PersonSalary.Filter.Benefits,
      filter.benefitsFrom,
      filter.benefitsTo
    );

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.PersonSalary.Filter.GrossSalary,
      filter.grossAmountFrom,
      filter.grossAmountTo
    );

    return reportFilter;
  };

  const filterFormFields: GenericFormSection<PersonSalaryFormTypes.Filter>[] = [
    {
      label: TranslationKeys.Common.Dates,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        yearMonth: {
          label: TranslationKeys.Common.RefersTo,
          type: FormEnums.FormFieldType.MONTHRANGE,
        },
        paymentDate: {
          label: TranslationKeys.Common.PaymentDate,
          type: FormEnums.FormFieldType.DATERANGE,
        },
      },
    },
    {
      label: TranslationKeys.Common.General,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        personName: {
          label: TranslationKeys.Common.Employee,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          extraProps: {
            referenceFieldName: FormFieldConstants.PersonSalary.Filter.PersonId,
            items: (searchStr: string) => companyService.SearchPersonsAutoComplete(searchStr, false),
          } as AutoCompleteExtraProps<PersonSalaryFormTypes.Filter>,
          hidden: !isSalaryAdmin,
        },
        state: {
          label: TranslationKeys.Common.Status,
          type: FormEnums.FormFieldType.MULTIPLESELECTION,
          extraProps: {
            items: EnumHelper.getNumericItemValues(WintSuperkollAPIModel.PersonSalaryViewState).map((state) => ({
              id: state,
              value: salaryService.getPersonSalaryStateText(state),
            })),
          },
        },
        isActive: {
          label: TranslationKeys.Common.Active,
          type: FormEnums.FormFieldType.BOOLEANSELECTION,
        },
      },
    },
    {
      label: TranslationKeys.Common.Amount,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        taxesAndFees: {
          label: TranslationKeys.Salary.TaxesAndFees,
          type: FormEnums.FormFieldType.CURRENCYRANGE,
          extraProps: {
            currency: CurrencyEnums.CurrencyCode.SEK,
          },
        },
        benefits: {
          label: TranslationKeys.Salary.Benefits,
          type: FormEnums.FormFieldType.CURRENCYRANGE,
          extraProps: {
            currency: CurrencyEnums.CurrencyCode.SEK,
          },
        },
        grossSalary: {
          label: TranslationKeys.Salary.GrossSalary,
          type: FormEnums.FormFieldType.CURRENCYRANGE,
          extraProps: {
            currency: CurrencyEnums.CurrencyCode.SEK,
          },
        },
      },
    },
  ];

  const filterQueryStateHandler: FilterQueryStateHandler<PersonSalaryFormTypes.Filter> = {
    personId: QueryStateHelper.numberQueryEncoder(FormFieldConstants.PersonSalary.Filter.PersonId),
    personName: QueryStateHelper.stringQueryEncoder(FormFieldConstants.PersonSalary.Filter.PersonName),
    yearMonth: QueryStateHelper.monthRangeQueryEncoder(FormFieldConstants.PersonSalary.Filter.YearMonth),
    paymentDate: QueryStateHelper.dateRangeQueryEncoder(FormFieldConstants.PersonSalary.Filter.PaymentDate),
    state: QueryStateHelper.selectionItemsQueryEncoder(
      FormFieldConstants.PersonSalary.Filter.State,
      EnumHelper.getNumericItemValues(WintSuperkollAPIModel.PersonSalaryViewState).map((state) => ({
        id: state,
        value: salaryService.getPersonSalaryStateText(state),
      }))
    ),
    taxesAndFees: QueryStateHelper.numberRangeQueryEncoder(FormFieldConstants.PersonSalary.Filter.TaxesAndFees),
    benefits: QueryStateHelper.numberRangeQueryEncoder(FormFieldConstants.PersonSalary.Filter.Benefits),
    grossSalary: QueryStateHelper.numberRangeQueryEncoder(FormFieldConstants.PersonSalary.Filter.GrossSalary),
    isActive: QueryStateHelper.booleanQueryEncoder(FormFieldConstants.PersonSalary.Filter.IsActive),
  };

  return { filterFormFields, filterQueryStateHandler, apiToReportFilterFormatter };
}

export default usePersonSalaryReportFilterDefinition;
